import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Mail, MapPin, CheckCircle2 } from "lucide-react";
import { Navbar } from "../components/sections/Navbar";
import { Footer } from "../components/sections/Footer";
import { ScrollProgress } from "../components/visuals/ScrollProgress";
import useReveal from "../lib/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const interestedInOptions = [
    { value: "overdose_response", label: "Overdose Response" },
    { value: "drink_spike_detection", label: "Drink Spike Detection" },
    { value: "ln2_monitoring", label: "LN₂ Monitoring" },
    { value: "alert_automation", label: "Alert & Automation" },
    { value: "custom_system", label: "Custom System" },
    { value: "other", label: "Other" },
];

const inquiryOptions = [
    { value: "demo", label: "I want to be a pilot partner" },
    { value: "partner", label: "Partnership" },
    { value: "invest", label: "Investor inquiry" },
    { value: "support", label: "I want to support your work" },
    { value: "general", label: "General inquiry" },
];

// Map query param ?interest=xxx and prefill dropdown
const INTEREST_QUERY_MAP = {
    "overdose-response": "overdose_response",
    "drink-spike-detection": "drink_spike_detection",
    "ln2-monitoring": "ln2_monitoring",
    "alert-automation": "alert_automation",
    "custom-system": "custom_system",
};

export default function ContactPage() {
    useReveal();
    const location = useLocation();
    const [form, setForm] = useState({
        name: "",
        organization: "",
        email: "",
        inquiry_type: "general",
        interested_in: "overdose_response",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    // Prefill from ?interest= and ?device= query params on load
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const raw = params.get("interest");
        const device = params.get("device");
        setForm((f) => {
            const next = { ...f };
            if (raw && INTEREST_QUERY_MAP[raw]) {
                next.interested_in = INTEREST_QUERY_MAP[raw];
            }
            if (device && !f.message) {
                next.message = `I'd like to inquire about the ${device}. `;
            }
            return next;
        });
    }, [location.search]);

    const onChange = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || form.message.length < 5) {
            toast.error("Please complete the required fields.");
            return;
        }
        try {
            setSubmitting(true);
            await axios.post(`${API}/contact`, form);
            setDone(true);
            toast.success("Message received. We'll be in touch shortly.");
            setForm((f) => ({
                ...f,
                name: "",
                organization: "",
                email: "",
                message: "",
            }));
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                "Something went wrong. Please try again.";
            toast.error(typeof msg === "string" ? msg : "Submission failed.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main data-testid="contact-page" className="min-h-screen bg-white">
            <ScrollProgress />
            <Navbar />

            {/* Hero */}
            <section className="relative bg-[#0A1428] text-white pt-32 md:pt-40 pb-20 md:pb-24 overflow-hidden">
                <div className="absolute inset-0 dot-grid-light opacity-25 pointer-events-none" />
                <div className="absolute -top-32 -right-24 w-[520px] h-[520px] opacity-[0.08] pointer-events-none float-slow">
                    <svg viewBox="0 0 200 200">
                        <polygon points="20,160 130,60 180,160" fill="#F0493E" />
                    </svg>
                </div>

                <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                    <Link
                        to="/"
                        data-testid="contact-back"
                        className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-slate-300 hover:text-white transition-colors mb-10"
                    >
                        <ArrowLeft size={16} strokeWidth={2} />
                        Back to Kairos
                    </Link>

                    <div className="text-xs md:text-sm font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                        Contact Kairos
                    </div>
                    <h1 className="mt-6 font-display font-extrabold text-4xl md:text-6xl lg:text-7xl leading-[1] tracking-tighter text-balance max-w-4xl">
                        Tell us where
                        <br />
                        to deploy next.
                    </h1>
                    <p className="mt-8 max-w-2xl text-lg md:text-xl text-slate-300 leading-relaxed font-medium">
                        Whether you&apos;re a venue, correctional facility,
                        healthcare system, lab, or municipality — we&apos;ll
                        walk you through what a Kairos deployment looks like in
                        your environment.
                    </p>
                </div>
            </section>

            {/* Form section */}
            <section className="relative bg-white py-24 md:py-28">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-12 gap-x-8 gap-y-16">
                        <div className="col-span-12 lg:col-span-5 reveal">
                            <span className="text-xs md:text-sm font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                                Direct
                            </span>
                            <h2 className="mt-6 font-display font-extrabold text-3xl md:text-4xl leading-[1.05] tracking-tighter text-[#0A1428]">
                                Start a conversation.
                            </h2>
                            <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed font-medium max-w-md">
                                Pick what you&apos;re interested in and give us a
                                bit of context. We reply within one business
                                day.
                            </p>

                            <div className="mt-10 space-y-6">
                                <InfoRow
                                    icon={Mail}
                                    label="Email"
                                    value="kairosord@gmail.com"
                                />
                                <InfoRow
                                    icon={MapPin}
                                    label="Operations"
                                    value="United States · Remote-first"
                                />
                            </div>
                        </div>

                        <form
                            data-testid="contact-form"
                            onSubmit={submit}
                            className="col-span-12 lg:col-span-7 reveal bg-slate-50 border border-slate-200 p-8 md:p-12 relative"
                        >
                            {done && (
                                <div
                                    data-testid="contact-success-card"
                                    className="absolute inset-0 bg-white/95 backdrop-blur-sm flex flex-col items-center justify-center text-center p-8 md:p-12 z-10 border border-slate-200"
                                >
                                    <div className="relative h-16 w-16 grid place-items-center bg-[#F0493E] rounded-full">
                                        <span className="absolute inset-0 rounded-full pulse-ring" />
                                        <CheckCircle2 size={32} strokeWidth={1.5} className="text-white relative" />
                                    </div>
                                    <h3 className="mt-6 font-display font-extrabold text-3xl md:text-4xl text-[#0A1428] tracking-tighter">
                                        Message received.
                                    </h3>
                                    <p className="mt-4 max-w-md text-base text-slate-600 font-medium leading-relaxed">
                                        We&apos;ve logged your inquiry and a
                                        member of the Kairos team will be in
                                        touch within one business day.
                                    </p>
                                    <button
                                        data-testid="contact-success-reset"
                                        onClick={() => setDone(false)}
                                        className="btn-ghost mt-8 inline-flex items-center px-6 py-3 text-sm font-bold tracking-wide uppercase rounded-sm"
                                    >
                                        Send another
                                    </button>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <Field label="Full name *">
                                    <input
                                        data-testid="contact-input-name"
                                        required
                                        value={form.name}
                                        onChange={onChange("name")}
                                        placeholder="Jane Doe"
                                        className="kairos-input"
                                    />
                                </Field>
                                <Field label="Organization">
                                    <input
                                        data-testid="contact-input-org"
                                        value={form.organization}
                                        onChange={onChange("organization")}
                                        placeholder="County Health Dept."
                                        className="kairos-input"
                                    />
                                </Field>
                                <Field label="Email *">
                                    <input
                                        data-testid="contact-input-email"
                                        required
                                        type="email"
                                        value={form.email}
                                        onChange={onChange("email")}
                                        placeholder="jane@org.gov"
                                        className="kairos-input"
                                    />
                                </Field>
                                <Field label="Interested in">
                                    <select
                                        data-testid="contact-input-interested"
                                        value={form.interested_in}
                                        onChange={onChange("interested_in")}
                                        className="kairos-input kairos-select pr-10"
                                    >
                                        {interestedInOptions.map((o) => (
                                            <option key={o.value} value={o.value}>
                                                {o.label}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                                <Field label="Inquiry type" className="md:col-span-2">
                                    <select
                                        data-testid="contact-input-inquiry"
                                        value={form.inquiry_type}
                                        onChange={onChange("inquiry_type")}
                                        className="kairos-input kairos-select pr-10"
                                    >
                                        {inquiryOptions.map((o) => (
                                            <option key={o.value} value={o.value}>
                                                {o.label}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                            </div>

                            <Field label="Message *" className="mt-6">
                                <textarea
                                    data-testid="contact-input-message"
                                    required
                                    value={form.message}
                                    onChange={onChange("message")}
                                    rows={5}
                                    placeholder="Tell us about your environment, scale, and timeline."
                                    className="kairos-input resize-none"
                                />
                            </Field>

                            <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <p className="text-xs text-slate-500 font-medium max-w-sm">
                                    By submitting you agree to be contacted by
                                    Kairos ORD regarding your inquiry. We never
                                    share your information.
                                </p>
                                <button
                                    data-testid="contact-form-submit"
                                    disabled={submitting}
                                    type="submit"
                                    className="btn-primary inline-flex items-center justify-center gap-3 px-8 py-4 text-sm font-bold tracking-wide uppercase rounded-sm disabled:opacity-60 disabled:translate-y-0"
                                >
                                    {submitting ? "Sending…" : "Send message"}
                                    <ArrowRight size={18} strokeWidth={2} />
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                <style>{`
                    .kairos-input {
                        width: 100%;
                        background: #ffffff;
                        border: 1px solid #E2E8F0;
                        border-radius: 2px;
                        padding: 14px 16px;
                        font-size: 15px;
                        font-family: 'Manrope', sans-serif;
                        color: #0A1428;
                        transition: border-color .2s ease, box-shadow .2s ease;
                    }
                    .kairos-input::placeholder { color: #94a3b8; }
                    .kairos-input:focus {
                        outline: none;
                        border-color: #0A1428;
                        box-shadow: 0 0 0 3px rgba(240,73,62,0.18);
                    }
                    .kairos-select {
                        appearance: none;
                        -webkit-appearance: none;
                        background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'><path d='M2 4l4 4 4-4' stroke='%230A1428' stroke-width='1.5' fill='none' stroke-linecap='round'/></svg>");
                        background-repeat: no-repeat;
                        background-position: right 1rem center;
                        background-size: 12px;
                    }
                `}</style>
            </section>

            <Footer />
        </main>
    );
}

const Field = ({ label, children, className = "" }) => (
    <label className={`block ${className}`}>
        <span className="block text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-2">
            {label}
        </span>
        {children}
    </label>
);

const InfoRow = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-4">
        <div className="h-10 w-10 grid place-items-center border border-slate-300">
            <Icon size={16} strokeWidth={1.5} className="text-[#0A1428]" />
        </div>
        <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
                {label}
            </div>
            <div className="mt-1 font-display font-bold text-[#0A1428] text-base">
                {value}
            </div>
        </div>
    </div>
);
